import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  FiBook,
  FiSearch,
  FiUser,
  FiUsers,
  FiDollarSign,
  FiAlertTriangle,
  FiCheckCircle,
  FiBarChart2,
  FiShoppingCart,
  FiTrendingUp,
  FiPlus,
  FiX,
  FiPhone,
  FiMail,
  FiEdit3,
  FiTrash2,
  FiRefreshCw,
  FiPrinter,
  FiCalendar,
  FiActivity,
  FiChevronDown,
  FiChevronUp,
  FiUserPlus,
  FiCreditCard,
} from 'react-icons/fi';
import Breadcrumb from '../../components/common/Breadcrumb';

/* ─── Mock Data ─────────────────────────────────────────────────────── */
const INITIAL_CUSTOMERS = [
  { id: 1, name: 'Kasun Perera',        phone: '077-123-4567', email: 'kasun@example.com',     creditLimit: 50000,  usedCredit: 12500,  joinDate: '2024-01-15' },
  { id: 2, name: 'Nimal Silva',          phone: '071-234-5678', email: 'nimal@example.com',     creditLimit: 25000,  usedCredit: 24000,  joinDate: '2024-02-20' },
  { id: 3, name: 'Sanduni Fernando',     phone: '076-345-6789', email: 'sanduni@example.com',   creditLimit: 75000,  usedCredit: 8000,   joinDate: '2024-03-05' },
  { id: 4, name: 'Ravindu Jayawardena',  phone: '070-456-7890', email: 'ravindu@example.com',   creditLimit: 100000, usedCredit: 55000,  joinDate: '2024-01-28' },
  { id: 5, name: 'Chamari Bandara',      phone: '078-567-8901', email: 'chamari@example.com',   creditLimit: 30000,  usedCredit: 0,      joinDate: '2024-04-10' },
  { id: 6, name: 'Thilina Rajapaksa',    phone: '072-678-9012', email: 'thilina@example.com',   creditLimit: 20000,  usedCredit: 19800,  joinDate: '2024-02-14' },
];

const MOCK_TRANSACTIONS = [
  { id: 'CB-001', customerId: 1, customerName: 'Kasun Perera',       date: '2025-03-20', invoice: 'INV-0042', amount: 4500,  type: 'credit', status: 'pending',  items: 3 },
  { id: 'CB-002', customerId: 2, customerName: 'Nimal Silva',         date: '2025-03-19', invoice: 'INV-0039', amount: 8200,  type: 'credit', status: 'pending',  items: 5 },
  { id: 'CB-003', customerId: 4, customerName: 'Ravindu Jayawardena', date: '2025-03-18', invoice: 'INV-0035', amount: 15000, type: 'credit', status: 'paid',     items: 7 },
  { id: 'CB-004', customerId: 1, customerName: 'Kasun Perera',        date: '2025-03-17', invoice: 'INV-0032', amount: 3200,  type: 'credit', status: 'pending',  items: 2 },
  { id: 'CB-005', customerId: 3, customerName: 'Sanduni Fernando',    date: '2025-03-15', invoice: 'INV-0028', amount: 6800,  type: 'credit', status: 'paid',     items: 4 },
  { id: 'CB-006', customerId: 6, customerName: 'Thilina Rajapaksa',   date: '2025-03-14', invoice: 'INV-0025', amount: 9500,  type: 'credit', status: 'overdue',  items: 6 },
  { id: 'CB-007', customerId: 2, customerName: 'Nimal Silva',          date: '2025-03-12', invoice: 'INV-0022', amount: 4100,  type: 'payment', status: 'paid',    items: 0 },
  { id: 'CB-008', customerId: 4, customerName: 'Ravindu Jayawardena', date: '2025-03-10', invoice: 'INV-0018', amount: 12000, type: 'credit', status: 'paid',     items: 8 },
];

const formatCurrency = (v) =>
  new Intl.NumberFormat('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

/* ─── Sub-components ────────────────────────────────────────────────── */

function StatCard({ icon: Icon, label, value, sub, color, darkMode }) {
  return (
    <div className={`rounded-lg p-2 shadow border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-100'}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-sm font-bold dark:text-white">{value}</p>
          {sub && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
    </div>
  );
}

function CreditBar({ used, limit, darkMode }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-blue-500' : 'bg-green-500';
  return (
    <div className="w-full">
      <div className={`h-1.5 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
        <div className={`h-1.5 rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{pct}% used</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending:  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    paid:     'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    overdue:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    payment:  'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${map[status] || map.pending}`}>
      {status}
    </span>
  );
}

/* ─── Add / Edit Customer Modal ─────────────────────────────────────── */
function CustomerFormModal({ darkMode, initial, onSave, onClose }) {
  const [form, setForm] = useState(initial || { name: '', phone: '', email: '', creditLimit: '' });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim())  e.name = 'Name is required';
    if (!form.phone.trim()) e.phone = 'Phone is required';
    if (!form.creditLimit || isNaN(Number(form.creditLimit)) || Number(form.creditLimit) <= 0)
      e.creditLimit = 'Enter a valid amount';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave({
      ...form,
      creditLimit: Number(form.creditLimit),
      usedCredit:  initial ? initial.usedCredit : 0,
      joinDate:    initial ? initial.joinDate    : new Date().toISOString().slice(0, 10),
      id:          initial ? initial.id          : Date.now(),
    });
  };

  const inputCls = (key) => `w-full px-3 py-2 text-sm border rounded-lg outline-none transition-colors ${
    errors[key] ? 'border-red-400' : ''
  } ${darkMode
    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500'
    : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
  }`;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-2">
      <div className={`w-full max-w-md rounded-xl shadow-xl p-4 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
              <FiUserPlus className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {initial ? 'Edit Customer' : 'Add New Customer'}
            </h3>
          </div>
          <button onClick={onClose} className={`p-1 rounded-lg ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
            <FiX className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: 'Full Name *',       key: 'name',        type: 'text',   ph: 'Customer name' },
            { label: 'Phone *',            key: 'phone',       type: 'tel',    ph: '07X-XXX-XXXX' },
            { label: 'Email',              key: 'email',       type: 'email',  ph: 'email@example.com' },
            { label: 'Credit Limit (Rs.) *', key: 'creditLimit', type: 'number', ph: '0.00' },
          ].map(({ label, key, type, ph }) => (
            <div key={key} className="space-y-0.5">
              <label className={`block text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{label}</label>
              <input
                type={type}
                value={form[key]}
                onChange={(e) => setForm(f => ({ ...f, [key]: e.target.value }))}
                placeholder={ph}
                className={inputCls(key)}
              />
              {errors[key] && <p className="text-xs text-red-500">{errors[key]}</p>}
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <button
            onClick={handleSave}
            className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-all"
          >
            {initial ? 'Update Customer' : 'Add Customer'}
          </button>
          <button
            onClick={onClose}
            className={`flex-1 py-2 text-xs font-medium rounded-lg border transition-all ${
              darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
            }`}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────── */
export default function CreditBook() {
  const { darkMode } = useSelector(state => state.ui);

  const [activeTab,      setActiveTab]      = useState('overview');
  const [search,         setSearch]         = useState('');
  const [txSearch,       setTxSearch]       = useState('');
  const [customers,      setCustomers]      = useState(INITIAL_CUSTOMERS);
  const [transactions,   setTransactions]   = useState(MOCK_TRANSACTIONS);
  const [showForm,       setShowForm]       = useState(false);
  const [editCustomer,   setEditCustomer]   = useState(null);
  const [expandedTx,     setExpandedTx]     = useState(null);
  const [txStatusFilter, setTxStatusFilter] = useState('all');
  const [sortField,      setSortField]      = useState('name');
  const [sortAsc,        setSortAsc]        = useState(true);

  /* ── summary stats ── */
  const stats = useMemo(() => {
    const totalLimit     = customers.reduce((s, c) => s + c.creditLimit, 0);
    const totalUsed      = customers.reduce((s, c) => s + c.usedCredit, 0);
    const nearLimit      = customers.filter(c => (c.usedCredit / c.creditLimit) >= 0.9).length;
    const pendingAmount  = transactions.filter(t => t.status === 'pending').reduce((s, t) => s + t.amount, 0);
    const overdueAmount  = transactions.filter(t => t.status === 'overdue').reduce((s, t) => s + t.amount, 0);
    return { totalLimit, totalUsed, available: totalLimit - totalUsed, nearLimit, pendingAmount, overdueAmount };
  }, [customers, transactions]);

  /* ── filtered customers ── */
  const filteredCustomers = useMemo(() => {
    let list = customers.filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.phone.includes(search) ||
      (c.email && c.email.toLowerCase().includes(search.toLowerCase()))
    );
    list = [...list].sort((a, b) => {
      let av = a[sortField], bv = b[sortField];
      if (typeof av === 'string') av = av.toLowerCase(), bv = bv.toLowerCase();
      return sortAsc ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
    return list;
  }, [customers, search, sortField, sortAsc]);

  /* ── filtered transactions ── */
  const filteredTx = useMemo(() => {
    return transactions.filter(t => {
      const matchSearch = t.customerName.toLowerCase().includes(txSearch.toLowerCase()) || t.invoice.toLowerCase().includes(txSearch.toLowerCase());
      const matchStatus = txStatusFilter === 'all' || t.status === txStatusFilter;
      return matchSearch && matchStatus;
    });
  }, [transactions, txSearch, txStatusFilter]);

  /* ── handlers ── */
  const handleSaveCustomer = (data) => {
    if (editCustomer) {
      setCustomers(prev => prev.map(c => c.id === data.id ? data : c));
    } else {
      setCustomers(prev => [data, ...prev]);
    }
    setShowForm(false);
    setEditCustomer(null);
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete this customer?')) setCustomers(prev => prev.filter(c => c.id !== id));
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortAsc(v => !v);
    else { setSortField(field); setSortAsc(true); }
  };

  const SortIcon = ({ field }) =>
    sortField === field
      ? sortAsc ? <FiChevronUp className="w-3 h-3 inline ml-0.5" /> : <FiChevronDown className="w-3 h-3 inline ml-0.5" />
      : null;

  /* ── shared styles ── */
  const theadCls = `text-left text-xs font-semibold tracking-wider py-2 px-2 ${
    darkMode ? 'text-gray-400 bg-gray-700' : 'text-gray-500 bg-gray-100'
  }`;
  const tdCls    = `py-2 px-2 text-sm border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`;
  const rowHover = `transition-colors ${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`;

  const tabs = [
    { id: 'overview',     name: 'Overview',     icon: FiBarChart2  },
    { id: 'customers',    name: 'Customers',     icon: FiUsers      },
    { id: 'transactions', name: 'Transactions',  icon: FiShoppingCart },
  ];

  return (
    <div className={`flex flex-col p-1 md:p-1 rounded-xl shadow-md h-full overflow-y-auto border ${
      darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    }`}>
      <Breadcrumb current="Reports / Credit Book" />

      {/* ── Header ── */}
      <div className="mt-2 mb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow">
              <FiBook className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">Credit Book</h1>
              <p className="text-xs text-gray-600 dark:text-gray-400">Manage customer credit limits and outstanding balances</p>
            </div>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => { setShowForm(true); setEditCustomer(null); }}
              className="flex items-center gap-1 px-2 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-all"
            >
              <FiUserPlus className="w-3 h-3" /> Add Customer
            </button>
            <button className={`flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg border transition-all ${
              darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}>
              <FiPrinter className="w-3 h-3" /> Print
            </button>
          </div>
        </div>

        {/* ── Summary Stats ── */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-1 md:gap-2 mb-2">
          <StatCard icon={FiUsers}       label="Total Customers"   value={customers.length}                       color="text-blue-600 dark:text-blue-400" darkMode={darkMode} />
          <StatCard icon={FiCreditCard}  label="Total Credit Limit" value={`Rs. ${formatCurrency(stats.totalLimit)}`}  color="text-purple-600 dark:text-purple-400" darkMode={darkMode} />
          <StatCard icon={FiTrendingUp}  label="Total Used"        value={`Rs. ${formatCurrency(stats.totalUsed)}`}  color="text-amber-600 dark:text-amber-400" darkMode={darkMode} />
          <StatCard icon={FiDollarSign}  label="Available Credit"  value={`Rs. ${formatCurrency(stats.available)}`} color="text-green-600 dark:text-green-400" darkMode={darkMode} />
          <StatCard icon={FiActivity}    label="Pending Amount"    value={`Rs. ${formatCurrency(stats.pendingAmount)}`} sub="Awaiting payment" color="text-yellow-600 dark:text-yellow-400" darkMode={darkMode} />
          <StatCard icon={FiAlertTriangle} label="Overdue / Near Limit" value={stats.overdueAmount > 0 ? `Rs. ${formatCurrency(stats.overdueAmount)}` : `${stats.nearLimit} accts`} color="text-red-600 dark:text-red-400" darkMode={darkMode} />
        </div>

        {/* ── Tabs ── */}
        <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <nav className="-mb-px flex gap-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 py-1.5 px-1 border-b-2 font-medium text-xs transition-colors ${
                  activeTab === tab.id
                    ? darkMode ? 'border-blue-500 text-blue-400' : 'border-blue-500 text-blue-600'
                    : darkMode ? 'border-transparent text-gray-400 hover:text-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto">
        <div className={`rounded-lg p-1 min-h-full ${darkMode ? 'bg-gray-700/30' : 'bg-gray-100'}`}>

          {/* ════ OVERVIEW TAB ════ */}
          {activeTab === 'overview' && (
            <div className="space-y-2">
              {/* Near-limit alert */}
              {stats.nearLimit > 0 && (
                <div className={`flex items-start gap-2 p-2 rounded-lg border ${
                  darkMode ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'
                }`}>
                  <FiAlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                  <div>
                    <p className={`text-xs font-semibold ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                      {stats.nearLimit} customer{stats.nearLimit > 1 ? 's' : ''} near credit limit (≥90%)
                    </p>
                    <p className={`text-xs mt-0.5 ${darkMode ? 'text-red-300/70' : 'text-red-600/70'}`}>
                      Consider reviewing their credit limits or outstanding balances.
                    </p>
                  </div>
                </div>
              )}

              {/* Credit utilisation breakdown */}
              <div className={`rounded-lg border p-3 ${darkMode ? 'bg-gray-700/40 border-gray-600' : 'bg-white border-gray-200'}`}>
                <h2 className={`text-xs font-bold mb-2 flex items-center gap-1.5 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <FiBarChart2 className="w-3.5 h-3.5 text-blue-500" /> Credit Utilisation by Customer
                </h2>
                <div className="space-y-2">
                  {customers.map(c => {
                    const pct = c.creditLimit > 0 ? Math.min(100, Math.round((c.usedCredit / c.creditLimit) * 100)) : 0;
                    const bar = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-blue-500' : 'bg-green-500';
                    return (
                      <div key={c.id} className="flex items-center gap-2">
                        <div className="w-24 truncate">
                          <p className={`text-xs font-medium truncate ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{c.name}</p>
                        </div>
                        <div className="flex-1">
                          <div className={`h-2 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                            <div className={`h-2 rounded-full ${bar}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                        <div className="w-20 text-right">
                          <p className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{pct}%</p>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Rs. {formatCurrency(c.usedCredit)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recent transactions */}
              <div className={`rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-700/40 border-gray-600' : 'bg-white border-gray-200'}`}>
                <div className={`px-3 py-2 flex items-center justify-between border-b ${darkMode ? 'border-gray-600' : 'border-gray-100'}`}>
                  <h2 className={`text-xs font-bold flex items-center gap-1.5 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <FiActivity className="w-3.5 h-3.5 text-blue-500" /> Recent Credit Transactions
                  </h2>
                  <button
                    onClick={() => setActiveTab('transactions')}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    View all
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                        {['Customer', 'Invoice', 'Date', 'Amount', 'Status'].map(h => (
                          <th key={h} className={`${theadCls}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {MOCK_TRANSACTIONS.slice(0, 5).map(t => (
                        <tr key={t.id} className={rowHover}>
                          <td className={`${tdCls} font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t.customerName}</td>
                          <td className={`${tdCls} font-mono ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t.invoice}</td>
                          <td className={`${tdCls} ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t.date}</td>
                          <td className={`${tdCls} font-semibold ${t.type === 'payment' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}`}>
                            {t.type === 'payment' ? '+' : '-'}Rs. {formatCurrency(t.amount)}
                          </td>
                          <td className={tdCls}><StatusBadge status={t.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ════ CUSTOMERS TAB ════ */}
          {activeTab === 'customers' && (
            <div>
              {/* Search */}
              <div className="mb-2">
                <div className="relative">
                  <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                  <input
                    type="text"
                    placeholder="Search customers..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={`w-full pl-7 pr-2 py-1.5 text-xs border rounded-lg outline-none ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' : 'bg-white border-gray-200 text-gray-900 focus:border-blue-500'
                    }`}
                  />
                </div>
              </div>

              {/* Table */}
              <div className={`rounded-lg border overflow-hidden ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                        {[
                          { label: 'Customer',      field: 'name' },
                          { label: 'Phone',         field: 'phone' },
                          { label: 'Credit Limit',  field: 'creditLimit' },
                          { label: 'Used',          field: 'usedCredit' },
                          { label: 'Available',     field: null },
                          { label: 'Utilisation',   field: null },
                          { label: 'Joined',        field: 'joinDate' },
                          { label: 'Actions',       field: null },
                        ].map(({ label, field }) => (
                          <th
                            key={label}
                            onClick={field ? () => toggleSort(field) : undefined}
                            className={`${theadCls} ${field ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                          >
                            {label} {field && <SortIcon field={field} />}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className={darkMode ? 'bg-gray-800' : 'bg-white'}>
                      {filteredCustomers.map(c => {
                        const avail = c.creditLimit - c.usedCredit;
                        return (
                          <tr key={c.id} className={rowHover}>
                            <td className={tdCls}>
                              <div className="flex items-center gap-1.5">
                                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                  {c.name.charAt(0)}
                                </div>
                                <div>
                                  <p className={`text-xs font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{c.name}</p>
                                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{c.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className={`${tdCls} ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{c.phone}</td>
                            <td className={`${tdCls} font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Rs. {formatCurrency(c.creditLimit)}</td>
                            <td className={`${tdCls} text-red-500 font-medium`}>Rs. {formatCurrency(c.usedCredit)}</td>
                            <td className={`${tdCls} font-medium ${avail > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>Rs. {formatCurrency(avail)}</td>
                            <td className={tdCls} style={{ minWidth: 100 }}>
                              <CreditBar used={c.usedCredit} limit={c.creditLimit} darkMode={darkMode} />
                            </td>
                            <td className={`${tdCls} ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{c.joinDate}</td>
                            <td className={tdCls}>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => { setEditCustomer(c); setShowForm(true); }}
                                  className="p-1 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                                  title="Edit"
                                >
                                  <FiEdit3 className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => handleDelete(c.id)}
                                  className="p-1 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
                                  title="Delete"
                                >
                                  <FiTrash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {filteredCustomers.length === 0 && (
                        <tr>
                          <td colSpan={8} className={`py-6 text-center text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            No customers found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ════ TRANSACTIONS TAB ════ */}
          {activeTab === 'transactions' && (
            <div>
              {/* Filters */}
              <div className="mb-2 flex flex-col sm:flex-row gap-1">
                <div className="relative flex-1">
                  <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                  <input
                    type="text"
                    placeholder="Search by customer or invoice..."
                    value={txSearch}
                    onChange={(e) => setTxSearch(e.target.value)}
                    className={`w-full pl-7 pr-2 py-1.5 text-xs border rounded-lg outline-none ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' : 'bg-white border-gray-200 text-gray-900 focus:border-blue-500'
                    }`}
                  />
                </div>
                <div className="flex gap-1">
                  {['all', 'pending', 'paid', 'overdue'].map(s => (
                    <button
                      key={s}
                      onClick={() => setTxStatusFilter(s)}
                      className={`px-2 py-1 rounded-lg text-xs font-semibold capitalize transition-all border ${
                        txStatusFilter === s
                          ? 'bg-blue-600 text-white border-blue-600'
                          : darkMode
                            ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Transactions table */}
              <div className={`rounded-lg border overflow-hidden ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                        {['Ref #', 'Customer', 'Invoice', 'Date', 'Items', 'Amount', 'Type', 'Status', ''].map(h => (
                          <th key={h} className={`${theadCls}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className={darkMode ? 'bg-gray-800' : 'bg-white'}>
                      {filteredTx.map(t => (
                        <React.Fragment key={t.id}>
                          <tr className={rowHover}>
                            <td className={`${tdCls} font-mono ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t.id}</td>
                            <td className={`${tdCls} font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t.customerName}</td>
                            <td className={`${tdCls} font-mono ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t.invoice}</td>
                            <td className={`${tdCls} ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t.date}</td>
                            <td className={`${tdCls} text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t.items || '—'}</td>
                            <td className={`${tdCls} font-bold ${t.type === 'payment' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}`}>
                              {t.type === 'payment' ? '+' : '-'}Rs. {formatCurrency(t.amount)}
                            </td>
                            <td className={tdCls}>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${
                                t.type === 'payment'
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                  : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                              }`}>
                                {t.type}
                              </span>
                            </td>
                            <td className={tdCls}><StatusBadge status={t.status} /></td>
                            <td className={tdCls}>
                              <button
                                onClick={() => setExpandedTx(expandedTx === t.id ? null : t.id)}
                                className={`p-1 rounded-lg ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                              >
                                {expandedTx === t.id ? <FiChevronUp className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />}
                              </button>
                            </td>
                          </tr>
                          {expandedTx === t.id && (
                            <tr>
                              <td colSpan={9} className={`px-3 py-2 ${darkMode ? 'bg-gray-700/50' : 'bg-blue-50'}`}>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                  {[
                                    { label: 'Transaction Ref', val: t.id },
                                    { label: 'Invoice Number',  val: t.invoice },
                                    { label: 'Amount',          val: `Rs. ${formatCurrency(t.amount)}` },
                                    { label: 'Status',          val: t.status, capitalize: true },
                                    { label: 'Customer',        val: t.customerName },
                                    { label: 'Date',            val: t.date },
                                    { label: 'Items in Bill',   val: t.items || 'N/A' },
                                    { label: 'Type',            val: t.type, capitalize: true },
                                  ].map(({ label, val, capitalize }) => (
                                    <div key={label}>
                                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
                                      <p className={`text-xs font-semibold ${capitalize ? 'capitalize' : ''} ${darkMode ? 'text-white' : 'text-gray-900'}`}>{val}</p>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                      {filteredTx.length === 0 && (
                        <tr>
                          <td colSpan={9} className={`py-6 text-center text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            No transactions found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals row */}
              <div className={`mt-2 grid grid-cols-3 gap-1`}>
                {[
                  { label: 'Total Credited',  val: filteredTx.filter(t => t.type === 'credit').reduce((s, t) => s + t.amount, 0),  color: 'text-blue-600 dark:text-blue-400' },
                  { label: 'Total Payments',  val: filteredTx.filter(t => t.type === 'payment').reduce((s, t) => s + t.amount, 0), color: 'text-green-600 dark:text-green-400' },
                  { label: 'Net Outstanding', val: filteredTx.filter(t => t.type === 'credit').reduce((s, t) => s + t.amount, 0) - filteredTx.filter(t => t.type === 'payment').reduce((s, t) => s + t.amount, 0), color: 'text-red-500' },
                ].map(({ label, val, color }) => (
                  <div key={label} className={`rounded-lg border p-2 text-center ${darkMode ? 'bg-gray-700/40 border-gray-600' : 'bg-white border-gray-200'}`}>
                    <p className={`text-sm font-bold ${color}`}>Rs. {formatCurrency(val)}</p>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── Add/Edit Modal ── */}
      {showForm && (
        <CustomerFormModal
          darkMode={darkMode}
          initial={editCustomer}
          onSave={handleSaveCustomer}
          onClose={() => { setShowForm(false); setEditCustomer(null); }}
        />
      )}
    </div>
  );
}